import React from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Copy, AlertTriangle } from 'lucide-react';
import { getSidName } from '../../../lib/nrcDecoder/constants';

// Result of a decode: the NRC itself, plus (when a full 7F/SID/NRC response was given)
// which service it was returned for. `sidCode` is omitted for a bare-NRC input.
const NrcResultCard = ({ entry, sidCode }) => {
  const sidName = sidCode !== undefined ? getSidName(sidCode) : null;

  const buildCopyText = () => {
    const lines = [
      `NRC: ${entry.hex}${entry.reserved ? '' : ` (${entry.decimal})`}`,
      `Name: ${entry.label}`,
    ];
    if (sidCode !== undefined) {
      lines.push(`Original Service ID: 0x${sidCode.toString(16).toUpperCase().padStart(2, '0')}${sidName ? ` (${sidName})` : ''}`);
    }
    lines.push(`Meaning: ${entry.meaning}`);
    if (entry.description) lines.push('', 'Description:', entry.description);
    if (entry.causes.length) lines.push('', 'Typical Causes:', ...entry.causes.map((c) => `• ${c}`));
    if (entry.troubleshooting.length) lines.push('', 'Troubleshooting:', ...entry.troubleshooting.map((t) => `• ${t}`));
    if (entry.services.length) lines.push('', 'Related UDS Service(s):', ...entry.services.map((s) => `• ${s}`));
    return lines.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyText());
      toast.success('Decoded result copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  return (
    <Card className="technical-card" data-testid="nrc-result-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-2xl font-bold" data-testid="nrc-result-hex">{entry.hex}</span>
              {!entry.reserved && <span className="font-mono text-sm text-muted-foreground">({entry.decimal} decimal)</span>}
              <Badge variant="outline" className="font-mono text-[11px]">{entry.category}</Badge>
              {entry.reserved && (
                <Badge variant="outline" className="font-mono text-[11px] text-amber-600 border-amber-600/40 bg-amber-600/10">
                  Reserved / Not Standardized
                </Badge>
              )}
            </div>
            <CardTitle className="font-chivo uppercase tracking-tight text-lg" data-testid="nrc-result-name">
              {entry.label}
            </CardTitle>
            {sidCode !== undefined && (
              <CardDescription className="font-mono text-xs">
                In response to Service ID 0x{sidCode.toString(16).toUpperCase().padStart(2, '0')}{sidName ? ` — ${sidName}` : ''}
              </CardDescription>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={handleCopy} aria-label="Copy decoded result" data-testid="nrc-copy-button">
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-base leading-relaxed" data-testid="nrc-result-meaning">{entry.meaning}</p>

        {entry.description && (
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Detailed Description</p>
            <p className="text-sm leading-relaxed text-foreground/90">{entry.description}</p>
          </div>
        )}

        {entry.reserved && (
          <div className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>This code falls in a reserved or vehicle-manufacturer-specific range ({entry.rangeLabel}). Its exact meaning here is intentionally not stated — check the target ECU&apos;s own OEM diagnostic specification.</span>
          </div>
        )}

        {entry.causes.length > 0 && (
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Typical Causes</p>
            <ul className="space-y-1 list-disc pl-5 text-sm leading-relaxed text-foreground/90">
              {entry.causes.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}

        {entry.troubleshooting.length > 0 && (
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Troubleshooting / What To Check</p>
            <ul className="space-y-1 list-disc pl-5 text-sm leading-relaxed text-foreground/90">
              {entry.troubleshooting.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
        )}

        {entry.services.length > 0 && (
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Related UDS Service(s)</p>
            <div className="flex flex-wrap gap-1.5">
              {entry.services.map((s, i) => (
                <Badge key={i} variant="outline" className="font-mono text-[11px] font-normal">{s}</Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NrcResultCard;
